import { useCallback, useEffect, useMemo, useRef } from 'react';
import { SECURITY_NOT_IN_ENTITY } from 'common/constants/messages/validations';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { getCashFlowColumns } from '../data/CashFlowColumnConfig';
import { CashFlowParser } from '../data/CashFlowParser';
import { CashFlowReverseParser } from '../data/CashFlowReverseParser';
import { CashFlowRowConfig } from '../data/CashFlowRowConfig';
import { CashFlowColumnAlias } from '../data/constants';
import { fromEntityValue, NO_ENTITY_FILTER } from '../data/entityValue';
import { CashFlowLedgerTableData, UseCashFlowLedgerSpreadsheetConfigProps } from '../data/types';

export const useCashDistributionsSpreadsheetConfig = ({
  ledgerData,
  activeEntities = NO_ENTITY_FILTER,
  companyId,
  isDisabled = false,
  securityOptionsByEntity,
  onDeleteRow,
  onPreFxHostPreview,
}: UseCashFlowLedgerSpreadsheetConfigProps) => {
  // Use ref to avoid recreating customValidations on every render
  const securityOptionsByEntityRef = useRef(securityOptionsByEntity);
  useEffect(() => {
    securityOptionsByEntityRef.current = securityOptionsByEntity;
  }, [securityOptionsByEntity]);

  // rowConfig now contains the actual data items (each row is a transaction)
  const rowConfig = useMemo(() => CashFlowRowConfig(ledgerData, activeEntities), [ledgerData, activeEntities]);

  // columns define the fields that will be shown for each row
  const columns = useMemo(() => getCashFlowColumns(), []);

  // Stable ref so the parser closure picks up the latest callback without
  // recreating the SpreadsheetConfig on every render (the framework caches
  // parsed cells and a parser-identity change would force a re-parse).
  const onPreFxHostPreviewRef = useRef(onPreFxHostPreview);
  useEffect(() => {
    onPreFxHostPreviewRef.current = onPreFxHostPreview;
  }, [onPreFxHostPreview]);

  // Wrap CashFlowParser so the spreadsheet framework's
  // `parser(params)` invocation also threads the preview callback. The
  // wrapper is stable across renders thanks to the ref indirection above.
  // Cast to `any` to match the framework's loose ParserParams contract -
  // the underlying parser already shares this widening with the original
  // `parser: CashFlowParser` assignment.
  const parser = useCallback(
    (params: any) =>
      CashFlowParser({
        ...params,
        onPreFxHostPreview: onPreFxHostPreviewRef.current
          ? (cellKey: string, value: number | null) => onPreFxHostPreviewRef.current?.(cellKey, value)
          : undefined,
      }),
    []
  );

  // Stable validation callback that uses ref
  const customValidations = useCallback((params: {
    cell: any;
    cellsToValidate?: any[];
    tableData?: CashFlowLedgerTableData;
    addFeedbackFromCell: (cell: any, feedback: string, validationType?: string) => void;
    removeFeedbackFromCell: (cell: any, feedback: string, validationType?: string) => void;
  }) => {
    const { cell, cellsToValidate, tableData, addFeedbackFromCell, removeFeedbackFromCell } = params;

    if (!cell || !cellsToValidate) {
      return;
    }

    const { alias, rowNumber } = cell;

    // Validate security belongs to entity
    if (alias === CashFlowColumnAlias.ALL_SECURITY || alias === CashFlowColumnAlias.ENTITY) {
      const { current: currentSecurityOptions } = securityOptionsByEntityRef;

      if (!currentSecurityOptions || Object.keys(currentSecurityOptions).length === 0) {
        return;
      }

      if (!rowNumber || rowNumber === 1) {
        return;
      }

      const entityCell = cellsToValidate.find(
        cellToCheck => cellToCheck.alias === CashFlowColumnAlias.ENTITY && cellToCheck.rowNumber === rowNumber
      );
      const securityCell = cellsToValidate.find(
        cellToCheck => cellToCheck.alias === CashFlowColumnAlias.ALL_SECURITY && cellToCheck.rowNumber === rowNumber
      );

      if (!entityCell || !securityCell) {
        return;
      }

      const { value: entityValue } = entityCell;
      const { value: securityValue } = securityCell;

      const entityId = fromEntityValue(entityValue, tableData?.companyId)?.entity_company;

      let securityId = null;
      if (typeof securityValue === 'number') {
        securityId = securityValue;
      } else if (typeof securityValue === 'string') {
        securityId = Number.parseInt(securityValue, 10);
      }

      if (!entityId || !securityId) {
        removeFeedbackFromCell(securityCell, SECURITY_NOT_IN_ENTITY);
        return;
      }

      const validSecurityOptions = currentSecurityOptions[entityId] ?? [];
      const securityIsValid = validSecurityOptions.some(({ value }) => Number(value) === securityId);

      if (securityIsValid) {
        removeFeedbackFromCell(securityCell, SECURITY_NOT_IN_ENTITY);
      } else {
        addFeedbackFromCell(securityCell, SECURITY_NOT_IN_ENTITY);
      }
    }
  }, []);

  // Built in the same render as its rows, so the sheet always matches the rows it is handed.
  const spreadsheet = useMemo<SpreadsheetConfig<object, object, object, object>>(
    () =>
      new SpreadsheetConfig({
        columns,
        name: 'CashDistributions',
        tableTerms: { tableSlug: 'cash-flow-ledger' },
        // Cast: the wrapped parser shares CashFlowParser's return type, which
        // the framework's loose `ParserParams` accepts only via the same
        // structural coercion that worked for `parser: CashFlowParser`. The
        // cast keeps the page-level callback wiring out of every consumer of
        // `SpreadsheetConfig`.
        parser: parser as any,
        reverseParser: CashFlowReverseParser,
        rowConfig,
        tableData: { companyId },
        format: {},
        customValidations,
        allowConfirmAndDeleteRow: !isDisabled,
        allowAddRow: !isDisabled,
        allowAddMultipleRows: !isDisabled,
        allowCopyRows: !isDisabled,
        displayDeleteRowBtn: !isDisabled,
        deleteRowFn: onDeleteRow || (() => { }),
        showToolbar: false,
        showTitlesColumn: false,
        showFirstRowAsTitle: false,
        currencyFormatter: false,
        unitsFormatter: false,
      }),
    [columns, companyId, customValidations, isDisabled, onDeleteRow, parser, rowConfig]
  );

  return {
    spreadsheet,
  };
};
